import React, { useState } from 'react';
import {
    Paper, Box, Typography, Button, Grid, TextField, Autocomplete, FormControl, InputLabel, Select, MenuItem, Chip, Stack, IconButton, Collapse, Checkbox, FormControlLabel, Divider, Switch, FormHelperText
} from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon, VerifiedUser as VerifiedUserIcon } from '@mui/icons-material';
import { resolveApprovedLocation } from '../createInquiryCalculation';

const TripDetailsCard = ({
    tripDetails,
    handleTripDetailsChange,
    setTripDetails,
    selectedPackage,
    stayInfo,
    handleStayInfoChange,
    setStayInfo,
    uniqueLocations,
    snackbar,
    configData,
    onReset,
    handleLocationChange,
    allPackages,
    onPackageSelect,
    carSeason,
    handleCarSeasonChange,
    handleCarDetailsChange,
    handleOptionalExtraToggle,
    handleOptionalExtraIncludeToggle,
    isLocationValid = true
}) => {
    const isPackageVerified = Boolean(selectedPackage && selectedPackage._id !== 'custom' && (selectedPackage.verified || selectedPackage.verifyed || selectedPackage.isVerified));

    const SIKKIM_EXTRAS = [
        { id: 'nathula', name: 'Nathu la', price: 4500, detail: 'With Tsongmo Lake' },
        { id: 'kalapatthar', name: 'Kala Patthar', price: 3000, detail: 'With Gurudongmar Lake' },
        { id: 'zeropoint', name: 'Zero Point', price: 4000, detail: 'With Yumthang Valley' },
        { id: 'mtkatao', name: 'Mt Katao', price: 3000, detail: 'On Lachung to Gangtok day' },
        { id: 'namchi_ravangla', name: 'Namchi and Ravangla', price: 3000, detail: 'On Gangtok to Pelling day' }
    ];
    const [isExpanded, setIsExpanded] = useState(true);
    const user = JSON.parse(localStorage.getItem('user'));
    // Helper function to get car count for a specific car type
    const getCarCount = (carName) => {
        const car = tripDetails.car_details?.find(c => c.car_name === carName);
        return car ? car.car_count : 0;
    };

    return (
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid #eee' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: isExpanded ? 3 : 0 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        flexWrap: 'wrap',
                        cursor: 'pointer',
                        flex: 1,
                        transition: 'opacity 0.2s',
                        '&:hover': { opacity: 0.8 }
                    }}
                    onClick={() => setIsExpanded(!isExpanded)}
                >
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#333' }}>
                        Trip Details
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                        {tripDetails.location && <Chip label={`Location: ${tripDetails.location}`} size="small" sx={{ borderColor: '#2196f3', color: '#1976d2', fontWeight: 500 }} variant="outlined" />}
                        <Chip
                            icon={isPackageVerified ? <VerifiedUserIcon sx={{ fontSize: '14px !important' }} /> : undefined}
                            label={`Package: ${selectedPackage?.label || 'Custom Package'}${isPackageVerified ? ' (Verified)' : ''}`}
                            size="small"
                            color={isPackageVerified ? 'success' : 'default'}
                            sx={isPackageVerified ? { fontWeight: 600 } : { borderColor: '#ff9800', color: '#ed6c02', fontWeight: 500 }}
                            variant="outlined"
                        />
                        {carSeason && <Chip label={`Car Season: ${carSeason === 'off_season_price' ? 'Off Season' : 'Season'}`} size="small" sx={{ borderColor: '#00bcd4', color: '#0097a7', fontWeight: 500 }} variant="outlined" />}
                        {tripDetails.car_details?.filter(car => car.car_count > 0).map((car, idx) => (
                            <Chip key={idx} label={`${car.car_name}: ${car.car_count}`} size="small" sx={{ borderColor: '#4caf50', color: '#2e7d32', fontWeight: 500 }} variant="outlined" />
                        ))}
                        {tripDetails.duration > 0 && <Chip label={`${tripDetails.duration} Nights & ${parseInt(tripDetails.duration) + 1} Days`} size="small" sx={{ borderColor: '#2196f3', color: '#1976d2', fontWeight: 500 }} variant="outlined" />}
                    </Stack>
                </Box>
                <Button
                    size="small"
                    color="warning"
                    onClick={onReset}
                    sx={{ fontWeight: 600, letterSpacing: '0.5px' }}
                >
                    RESET
                </Button>
            </Box>
            <Collapse in={isExpanded}>
                <Grid container spacing={2.5}>
                    {/* Location and Package Row */}
                    <Grid item xs={12} md={6}>
                        {(() => {
                            const locList = (uniqueLocations || []).map(l => (typeof l === 'string' ? l : l.name)).filter(Boolean);
                            const rawLoc = (tripDetails.location || '').trim();
                            const matchedLoc = locList.find(l => l.toLowerCase() === rawLoc.toLowerCase()) ||
                                (rawLoc && uniqueLocations?.length > 0 ? resolveApprovedLocation(rawLoc, uniqueLocations) : '');
                            const currentLocValue = matchedLoc || rawLoc;
                            const hasLocError = Boolean(!currentLocValue || (currentLocValue && !isLocationValid));

                            return (
                                <FormControl
                                    fullWidth
                                    size="small"
                                    required
                                    error={hasLocError}
                                >
                                    <InputLabel id="trip-details-location-label">Location</InputLabel>
                                    <Select
                                        labelId="trip-details-location-label"
                                        id="trip-details-location-select"
                                        value={currentLocValue}
                                        label="Location"
                                        onChange={(e) => {
                                            const selectedName = e.target.value;
                                            if (handleLocationChange) {
                                                handleLocationChange(selectedName);
                                            } else {
                                                setTripDetails({ ...tripDetails, location: selectedName, duration: 0 });
                                            }
                                        }}
                                    >
                                        <MenuItem value="" disabled>
                                            <em>-- Select Approved Location Only --</em>
                                        </MenuItem>
                                        {locList.map((locName) => (
                                            <MenuItem key={locName} value={locName}>
                                                📍 {locName}
                                            </MenuItem>
                                        ))}
                                        {Boolean(currentLocValue && !locList.some(l => l.toLowerCase() === currentLocValue.toLowerCase())) && (
                                            <MenuItem key={currentLocValue} value={currentLocValue}>
                                                📍 {currentLocValue}
                                            </MenuItem>
                                        )}
                                    </Select>
                                    {hasLocError && (
                                        <FormHelperText error sx={{ mx: 0.5, mt: 0.5, fontSize: 11 }}>
                                            {!currentLocValue
                                                ? "Required: only approved locations can be selected"
                                                : (!isLocationValid ? "Invalid location: only approved locations allowed" : "")
                                            }
                                        </FormHelperText>
                                    )}
                                </FormControl>
                            );
                        })()}
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Autocomplete
                            size="small"
                            disabled={!isLocationValid}
                            disableClearable
                            options={[
                                { _id: 'custom', label: 'Custom Package', verified: false },
                                ...(allPackages
                                    ?.filter(p => {
                                        if (!tripDetails.location) return true;
                                        const pLoc = String(p.location || '').trim().toLowerCase();
                                        const tLoc = String(tripDetails.location || '').trim().toLowerCase();
                                        return pLoc === tLoc || pLoc.includes(tLoc) || tLoc.includes(pLoc);
                                    })
                                    .map(pkg => ({
                                        _id: pkg._id,
                                        label: pkg.label || pkg.name || 'Unnamed Package',
                                        verified: Boolean(pkg.verified || pkg.verifyed || pkg.isVerified),
                                        originalPkg: pkg
                                    })) || [])
                            ]}
                            getOptionLabel={(option) => option.label || ''}
                            renderOption={(props, option) => (
                                <li {...props} key={option._id}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                        <Typography variant="body2" sx={{ fontWeight: option.verified ? 600 : 400 }}>
                                            {option.label}
                                        </Typography>
                                        {option.verified && (
                                            <Chip
                                                icon={<VerifiedUserIcon sx={{ fontSize: '13px !important' }} />}
                                                label="Verified"
                                                size="small"
                                                color="success"
                                                variant="filled"
                                                sx={{ height: 20, fontSize: 10, fontWeight: 700, ml: 1 }}
                                            />
                                        )}
                                    </Box>
                                </li>
                            )}
                            value={
                                selectedPackage
                                    ? { _id: selectedPackage._id, label: selectedPackage.label || selectedPackage.name || 'Unnamed Package', verified: isPackageVerified, originalPkg: selectedPackage }
                                    : { _id: 'custom', label: 'Custom Package', verified: false }
                            }
                            isOptionEqualToValue={(option, value) => option._id === value?._id}
                            onChange={(e, newValue) => {
                                if (onPackageSelect) {
                                    if (!newValue || newValue._id === 'custom') {
                                        onPackageSelect(null);
                                    } else {
                                        onPackageSelect(newValue.originalPkg);
                                    }
                                }
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Package"
                                    placeholder={!isLocationValid ? "Select location first..." : "Search package"}
                                    helperText={
                                        !isLocationValid ? (
                                            <Box component="span" sx={{ color: '#d97706', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                                                ⚠️ Location selection is required before choosing a package
                                            </Box>
                                        ) : (
                                            selectedPackage && selectedPackage._id !== 'custom' ? (
                                                isPackageVerified ? (
                                                    <Box component="span" sx={{ color: '#16a34a', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                                                        <VerifiedUserIcon sx={{ fontSize: 13 }} /> Verified package — cannot update directly, edits create a new package
                                                    </Box>
                                                ) : (
                                                    <Box component="span" sx={{ color: '#64748b', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                                                        Not verified — edits can update this package or create a new one
                                                    </Box>
                                                )
                                            ) : null
                                        )
                                    }
                                />
                            )}
                        />
                    </Grid>



                    {/* Travel Date and Duration Row */}
                    < Grid item xs={12} md={6} >
                        <TextField
                            fullWidth
                            size="small"
                            label="Travel Date"
                            name="travel_date"
                            type="date"
                            value={tripDetails.travel_date}
                            onChange={handleTripDetailsChange}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid >
                    <Grid item xs={12} md={6}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500, fontSize: 11, ml: 0.5 }}>
                                Duration (Nights)
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #c4c4c4', borderRadius: 1.5, overflow: 'hidden', height: 40 }}>
                                <IconButton
                                    size="small"
                                    onClick={() => handleTripDetailsChange({ target: { name: 'duration', value: Math.max(0, (parseInt(tripDetails.duration) || 0) - 1) } })}
                                    sx={{ borderRadius: 0, height: '100%', width: 44, color: '#ef4444', bgcolor: '#fff5f5', '&:hover': { bgcolor: '#fee2e2' }, flexShrink: 0 }}
                                >
                                    <RemoveIcon fontSize="small" />
                                </IconButton>
                                <Box sx={{ flex: 1, textAlign: 'center' }}>
                                    <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#1e293b', lineHeight: 1 }}>
                                        {parseInt(tripDetails.duration) || 0}
                                    </Typography>
                                    <Typography sx={{ fontSize: 9, color: '#94a3b8', lineHeight: 1 }}>
                                        {(parseInt(tripDetails.duration) || 0) > 0 ? `${(parseInt(tripDetails.duration) || 0) + 1} Days` : 'nights'}
                                    </Typography>
                                </Box>
                                <IconButton
                                    size="small"
                                    onClick={() => handleTripDetailsChange({ target: { name: 'duration', value: (parseInt(tripDetails.duration) || 0) + 1 } })}
                                    sx={{ borderRadius: 0, height: '100%', width: 44, color: '#22c55e', bgcolor: '#f0fdf4', '&:hover': { bgcolor: '#dcfce7' }, flexShrink: 0 }}
                                >
                                    <AddIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        </Box>
                    </Grid>

                    {/* Rooms and Hotel Type Row */}
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Number of Rooms"
                            name="rooms"
                            type="number"
                            value={stayInfo.rooms}
                            onChange={handleStayInfoChange}
                            inputProps={{ min: 0 }}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Extra Mattress"
                            name="extra_mattress"
                            type="number"
                            value={stayInfo.extra_mattress !== undefined ? stayInfo.extra_mattress : ''}
                            onChange={handleStayInfoChange}
                            inputProps={{ min: 0 }}
                            placeholder="Auto if empty"
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Room Type</InputLabel>
                            <Select
                                name="room_type"
                                value={stayInfo.room_type || ""}
                                onChange={handleStayInfoChange}
                                label="Room Type"
                            >
                                {["Standard", "Deluxe", "Super Deluxe", "Luxury", "Suite", "Family Room"].map((type) => (
                                    <MenuItem key={type} value={type}>{type}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Pickup and Dropoff Row */}
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Pickup Location"
                            name="pickup_location"
                            value={tripDetails.pickup_location || ''}
                            onChange={handleTripDetailsChange}
                            placeholder="NJP / IXB"
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Dropoff Location"
                            name="dropoff_location"
                            value={tripDetails.dropoff_location || ''}
                            onChange={handleTripDetailsChange}
                            placeholder="NJP / IXB"
                        />
                    </Grid>
                    {(tripDetails?.location?.toLowerCase() === 'sikkim' || tripDetails?.location?.toLowerCase()?.includes('sikkim')) && (
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                                    Optional Extras (Sikkim)
                                </Typography>
                            </Box>
                            <Grid container spacing={1}>
                                {SIKKIM_EXTRAS.map((extra) => {
                                    const selectedExtra = (tripDetails.optional_extras || []).find(e => {
                                        const storedName = typeof e === 'object' ? (e.name || '') : e;
                                        return (storedName || '').toLowerCase().trim() === extra.name.toLowerCase().trim();
                                    });
                                    const isSelected = !!selectedExtra;
                                    const count = selectedExtra?.count || 1;

                                    return (
                                        <Grid item xs={12} sm={6} md={4} lg={3} key={extra.id}>
                                            <Box sx={{ border: '1px solid #eee', p: 1, borderRadius: 1, height: '100%' }}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            size="small"
                                                            checked={isSelected}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    handleOptionalExtraToggle(extra.name, extra.price, 1);
                                                                } else {
                                                                    handleOptionalExtraToggle(extra.name, extra.price, 0);
                                                                }
                                                            }}
                                                        />
                                                    }
                                                    label={
                                                        <Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                {extra.name} (₹{extra.price.toLocaleString()}/-)
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {extra.detail}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                />
                                                {isSelected && (
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1, ml: 3 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', backgroundColor: '#f5f5f5', borderRadius: 1, width: 'fit-content', mb: 1 }}>
                                                            <IconButton 
                                                                size="small" 
                                                                onClick={() => handleOptionalExtraToggle(extra.name, extra.price, Math.max(1, count - 1))}
                                                                disabled={count <= 1}
                                                            >
                                                                <RemoveIcon fontSize="small" />
                                                            </IconButton>
                                                            <Typography variant="body2" sx={{ mx: 1, fontWeight: 'bold' }}>{count}</Typography>
                                                            <IconButton 
                                                                size="small" 
                                                                onClick={() => handleOptionalExtraToggle(extra.name, extra.price, count + 1)}
                                                            >
                                                                <AddIcon fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                        <FormControlLabel
                                                            control={
                                                                <Switch 
                                                                    size="small" 
                                                                    checked={selectedExtra.include_in_cost !== false} 
                                                                    onChange={(e) => handleOptionalExtraIncludeToggle(extra.name, e.target.checked)}
                                                                    color="primary"
                                                                />
                                                            }
                                                            label={<Typography variant="caption" sx={{ fontWeight: 600 }}>Include Cost</Typography>}
                                                        />
                                                    </Box>
                                                )}
                                            </Box>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                            <Divider sx={{ my: 2 }} />
                        </Grid>
                    )}

                    {/* Keywords Row */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Keywords"
                            name="keywords"
                            value={tripDetails.keywords}
                            onChange={handleTripDetailsChange}
                            placeholder="e.g., adventure, honeymoon"
                        />
                    </Grid>
                </Grid >
            </Collapse >
        </Paper >
    );
};

export default TripDetailsCard;
